
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Playlist {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
}

export interface PlaylistVideo {
  playlist_id: string;
  video_id: string;
}

// Define the payload types for better type safety
interface RealtimePayload {
  new: Record<string, any> | null;
  old: Record<string, any> | null;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
}

export const usePlaylistRealtime = (userId: string | undefined) => {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [playlistVideos, setPlaylistVideos] = useState<{[key: string]: string[]}>({});
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Load initial playlists
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    
    const fetchPlaylists = async () => {
      try {
        setIsLoading(true);
        
        // Get playlists
        const { data: playlistsData, error: playlistsError } = await supabase
          .from('playlists')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        
        if (playlistsError) throw playlistsError;
        
        console.log('Fetched playlists:', playlistsData);
        setPlaylists(playlistsData || []);
        
        // Get videos for each playlist
        const playlistsVideosMap: {[key: string]: string[]} = {};
        
        if (playlistsData && playlistsData.length > 0) {
          await Promise.all(playlistsData.map(async (playlist) => {
            const { data: playlistItems, error: playlistError } = await supabase
              .from('playlist_items')
              .select('video_id')
              .eq('playlist_id', playlist.id);
            
            if (playlistError) throw playlistError;
            
            const videoIds = playlistItems.map(item => item.video_id);
            console.log(`Playlist ${playlist.id} (${playlist.name}) video IDs:`, videoIds);
            playlistsVideosMap[playlist.id] = videoIds;
          }));
        }
        
        setPlaylistVideos(playlistsVideosMap);

        // Get favorites
        const { data: favoritesData, error: favoritesError } = await supabase
          .from('favorites')
          .select('video_id')
          .eq('user_id', userId);
        
        if (favoritesError) throw favoritesError;
        
        const favoriteIds = favoritesData?.map(fav => fav.video_id) || [];
        console.log('Fetched favorites:', favoriteIds);
        setFavorites(favoriteIds);
        
      } catch (error) {
        console.error('Error fetching playlists:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les playlists',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPlaylists();
  }, [userId, toast]);

  // Subscribe to real-time changes for playlists and items
  useEffect(() => {
    if (!userId) return;
    
    console.log('Setting up realtime subscriptions for user:', userId);
    
    // Channel for playlist updates
    const playlistsChannel = supabase
      .channel('playlists-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'playlists',
        filter: `user_id=eq.${userId}`
      }, (payload: RealtimePayload) => {
        console.log('Playlists change received:', payload);
        
        // Handle different event types
        if (payload.eventType === 'INSERT' && payload.new) {
          setPlaylists(current => [payload.new as Playlist, ...current]);
          setPlaylistVideos(current => ({
            ...current,
            [payload.new!.id]: []
          }));
        } else if (payload.eventType === 'DELETE' && payload.old) {
          setPlaylists(current => current.filter(p => p.id !== payload.old!.id));
          setPlaylistVideos(current => {
            const updated = { ...current };
            delete updated[payload.old!.id];
            return updated;
          });
        } else if (payload.eventType === 'UPDATE' && payload.new) {
          setPlaylists(current => 
            current.map(p => p.id === payload.new!.id ? payload.new as Playlist : p)
          );
        }
      })
      .subscribe((status) => {
        console.log('Playlists channel status:', status);
      });
    
    // Channel for playlist item updates - IMPROVED
    const playlistItemsChannel = supabase
      .channel('playlist-items-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'playlist_items'
      }, async (payload: RealtimePayload) => {
        console.log('Playlist items change received:', payload);
        
        // Safely access playlist_id from payload using type guards
        const playlistId = payload.new?.playlist_id || payload.old?.playlist_id;
        
        if (!playlistId) {
          console.error('Could not determine playlist_id from payload:', payload);
          return;
        }
        
        try {
          // Get updated list of videos for this playlist
          const { data: playlistItems, error } = await supabase
            .from('playlist_items')
            .select('video_id')
            .eq('playlist_id', playlistId);
          
          if (error) throw error;
          
          const videoIds = playlistItems.map(item => item.video_id);
          console.log(`Updated video list for playlist ${playlistId}:`, videoIds);
          
          // Update the list of videos for this playlist
          setPlaylistVideos(current => ({
            ...current,
            [playlistId]: videoIds
          }));
          
          // Show toast notification based on event type
          if (payload.eventType === 'INSERT') {
            toast({
              title: 'Vidéo ajoutée',
              description: 'La vidéo a été ajoutée à la playlist'
            });
          } else if (payload.eventType === 'DELETE') {
            toast({
              title: 'Vidéo retirée',
              description: 'La vidéo a été retirée de la playlist'
            });
          }
        } catch (error) {
          console.error('Error fetching updated playlist items:', error);
        }
      })
      .subscribe((status) => {
        console.log('Playlist items channel status:', status);
      });

    // Channel for favorites updates
    const favoritesChannel = supabase
      .channel('favorites-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'favorites',
        filter: `user_id=eq.${userId}`
      }, (payload: RealtimePayload) => {
        console.log('Favorites change received:', payload);
        
        if (payload.eventType === 'INSERT' && payload.new) {
          setFavorites(current => [...current, payload.new!.video_id]);
          toast({
            title: 'Ajouté aux favoris',
            description: 'La vidéo a été ajoutée à vos favoris'
          });
        } else if (payload.eventType === 'DELETE' && payload.old) {
          setFavorites(current => current.filter(id => id !== payload.old!.video_id));
          toast({
            title: 'Retiré des favoris',
            description: 'La vidéo a été retirée de vos favoris'
          });
        }
      })
      .subscribe((status) => {
        console.log('Favorites channel status:', status);
      });
    
    // Clean up subscriptions
    return () => {
      console.log('Cleaning up realtime subscriptions');
      supabase.removeChannel(playlistsChannel);
      supabase.removeChannel(playlistItemsChannel);
      supabase.removeChannel(favoritesChannel);
    };
  }, [userId, toast]);

  return {
    playlists,
    playlistVideos,
    favorites,
    isLoading
  };
};
